import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { GameData } from "../Channels/Common/Datas/GameData";
import { DataManager } from "../Managers/DataManager";
import { ModuleManager } from "../Managers/ModuleManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { ReelExtended } from "./ReelExtended";
import { Settings } from "./Settings";
import { WinAnimatorExtended } from "./WinAnimatorExtended";
import { GroupTweens } from "./GroupTweens";
import { Symbols } from "../GameComponents/Symbols";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Configuration } from "../Components/Configuration";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { AppContext } from "../../Common/AppContext";

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    static RollFinished: string = "rollFinished";
    static OneReelRollFinished: string = "oneReelRollFinished";

    private _symbolsNoTrimContainer: ContainerElement;
    private _forced: boolean = false;
    protected _finishedRollReelIndexes: number = 0;

    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        winAnimator: WinAnimator,
        teasing?: ContainerElement,
        symbolsNoTrimContainer?: ContainerElement,
    ): void {
        this._symbolsNoTrimContainer = symbolsNoTrimContainer; // this must be call before super.initialize (include create reels)

        super.initialize(symbols, reels, winAnimator, teasing);
    }

    protected createReels(reels: Array<Array<number>>): void {
        for (let i: number = 0; i < this._reelCount; i++) {
            this._reels.push(new ReelExtended());
        }

        for (let i: number = 0; i < this._reelCount; i++) {
            const reel = this._reels[i];

            if (this._reelConfiguration != null) {
                let configs: Array<string> = this._reelConfiguration.split(";");
                for (let j: number = 0; j < configs.length; j++) {
                    reel.configure(Configuration.Configurations[configs[j]]);
                }
            }
            reel.visible = false;
            reel.initialize(
                i > 0 ? this._reels[i - 1] : null,
                i < this._reelCount - 1 ? this._reels[i + 1] : null,
                i,
                this._symbols,
                reels[i],
                this._reelX[i],
                this._reelY[i],
                this._visibleSymbolCount,
                this._symbolTopCount,
                this._symbolBottomCount,
                this._symbolWidth,
                this._symbolHeight,
                this._startDelay * i,
                this._stopDelay * i,
                this._symbolsNoTrimContainer,
            );
            reel.on(Reel.ReelStopped, this.onReelStopped.bind(this));
            reel.on(ReelExtended.ReelRollFinished, this.onReelRollFinished.bind(this));
            this._reelsContainer.addChild(reel);
        }
        this._reelsContainer.visible = false;
    }

    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
    ): void {
        this._finishedRollReelIndexes = 0;
        this._forced = false;
        super.spinStart(longRotation, useTransform, currentSymbols);
    }

    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            PlayList.stop("SND_BaseFSTeaser");

            this._forced = true;

            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (DEBUG) {
            this.log("spinStop: " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !ModuleManager.freespinsActive() &&
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }
            if (force && this._teasing != null) {
                for (let key in this._teasing.childs) {
                    if (this._teasing.childs[key] instanceof SwitchElement) {
                        this._teasing.childs[key].goToCase("EMPTY");
                    }
                }
            }

            this._canTease2 = force ? false : this.checkTeasing(2);
            this._canTease3 = force ? false : this.checkTeasing(3);
            this._canTease4 = force ? false : this.checkTeasing(4);

            let teasingSymbolCounter: number = 0;

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            let stopDelay = this._stopDelay * i;
                            let useForceCustomDelay = false;

                            // --------- teasing HIT ----------
                            teasingSymbolCounter +=
                                this._stoppedSymbolsArray[i].symbols.indexOf(Settings.SymbolTeasing) !== -1 ? 1 : 0;
                            this._reels[i].countOfTeasingSymbols = teasingSymbolCounter;
                            this._reels[i].canTeasingSymbolHit =
                                (i == 3 && teasingSymbolCounter <= 1) || (i == 4 && teasingSymbolCounter <= 2)
                                    ? false
                                    : true;

                            if (force || (!this._canTease2 && !this._canTease3 && !this._canTease4)) {
                                this._reels[i].spinStop(force, stopDelay, useForceCustomDelay);
                            } else {
                                if (this._canTease2 && this._canTease3 && this._canTease4) {
                                    if (i === 0 || i === 1) {
                                        this._reels[i].spinStop(force, stopDelay, useForceCustomDelay);
                                    }
                                } else if (this._canTease3 && this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2) {
                                        this._reels[i].spinStop(force, stopDelay, useForceCustomDelay);
                                    }
                                } else if (this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2 || i === 3) {
                                        this._reels[i].spinStop(force, stopDelay, useForceCustomDelay);
                                    }
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    protected checkTeasing(reelIndex: number): boolean {
        let count = 0;

        for (let i = 0; i < reelIndex; i++) {
            for (let j = 0; j <= this._stoppedSymbolsArray[i].symbols.length; j++) {
                if (this._stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                    count++;
                }
            }
        }

        return count >= 2;
    }

    protected tease(reelIndex: number): void {
        switch (reelIndex) {
            case 1:
                if (this._canTease2) {
                    ReelExtended.Tease = true;
                    this._reels[2].spinTease([Settings.SymbolTeasing], 120);

                    // PlayList.stop('SND_BaseLoop');
                    PlayList.setVolume("SND_BaseLoop", 0);
                    PlayList.stop("SND_FSLoop");
                    PlayList.play("SND_BaseTeaser");

                    if (this._teasing != null) {
                        <SwitchElement>this._teasing.childs["Teasing3"].goToCase("START");

                        PlayList.play("SND_BaseTeaser01");

                        if (this.winAnimator != null && (<WinAnimatorExtended>this.winAnimator).groupTweens != null) {
                            (<WinAnimatorExtended>this.winAnimator).groupTweens.goToCase(GroupTweens.CaseTeasing);
                        }
                    }
                }

                break;
            case 2:
                if (this._canTease3) {
                    ReelExtended.Tease = true;
                    this._reels[3].spinTease([Settings.SymbolTeasing], 120);

                    if (!this._canTease2) {
                        // PlayList.stop('SND_BaseLoop');
                        PlayList.setVolume("SND_BaseLoop", 0);
                        PlayList.stop("SND_FSLoop");
                        PlayList.play("SND_BaseTeaser");
                    }

                    if (this._teasing != null) {
                        if ((<SwitchElement>this._teasing.childs["Teasing3"]).getCurrentCase() == "LOOP") {
                            (<SwitchElement>this._teasing.childs["Teasing3"]).goToCase("STOPPING");

                            PlayList.stop("SND_BaseTeaser01", 30);
                        }
                        (<SwitchElement>this._teasing.childs["Teasing4"]).goToCase("START");

                        PlayList.play("SND_BaseTeaser02");

                        if (this.winAnimator != null && (<WinAnimatorExtended>this.winAnimator).groupTweens != null) {
                            (<WinAnimatorExtended>this.winAnimator).groupTweens.goToCase(GroupTweens.CaseTeasing);
                        }
                    }
                }

                break;
            case 3:
                if (this._canTease4) {
                    ReelExtended.Tease = true;
                    this._reels[4].spinTease([Settings.SymbolTeasing], 120);

                    if (!this._canTease3) {
                        // PlayList.stop('SND_BaseLoop');
                        PlayList.setVolume("SND_BaseLoop", 0);
                        PlayList.stop("SND_FSLoop");
                        PlayList.play("SND_BaseTeaser");
                    }

                    if (this._teasing != null) {
                        if ((<SwitchElement>this._teasing.childs["Teasing4"]).getCurrentCase() == "LOOP") {
                            (<SwitchElement>this._teasing.childs["Teasing4"]).goToCase("STOPPING");

                            PlayList.stop("SND_BaseTeaser02", 30);
                        }
                        (<SwitchElement>this._teasing.childs["Teasing5"]).goToCase("START");

                        PlayList.play("SND_BaseTeaser03");

                        if (this.winAnimator != null && (<WinAnimatorExtended>this.winAnimator).groupTweens != null) {
                            (<WinAnimatorExtended>this.winAnimator).groupTweens.goToCase(GroupTweens.CaseTeasing);
                        }
                    }
                }

                break;
            case 4:
                PlayList.stop("SND_BaseTeaser", this._forced ? 10 : 60);

                if (this._canTease4) {
                    let count = 0;

                    for (let i = 0; i <= reelIndex; i++) {
                        for (let j = 0; j <= this._stoppedSymbolsArray[i].symbols.length; j++) {
                            if (this._stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                count++;
                            }
                        }
                    }

                    if (count >= 3) {
                        PlayList.stop("SND_BaseLoop");
                        PlayList.play("SND_BaseTeaserWin");
                    } else {
                        PlayList.play("SND_BaseTeaserFail");
                        PlayList.resetVolume("SND_BaseLoop");
                    }

                    if (this._teasing != null) {
                        if ((<SwitchElement>this._teasing.childs["Teasing5"]).getCurrentCase() == "LOOP") {
                            (<SwitchElement>this._teasing.childs["Teasing5"]).goToCase("STOPPING");

                            PlayList.stop("SND_BaseTeaser01");
                            PlayList.stop("SND_BaseTeaser02");
                            PlayList.stop("SND_BaseTeaser03", 30);
                        }
                    }
                }

                break;
        }
    }

    protected onReelRollFinished(reelIndex: number): void {
        this._finishedRollReelIndexes++;

        PlayList.play("SND_BaseRotationEnd" + reelIndex);

        this.tease(reelIndex);

        if (ReelExtended.Tease) {
            for (let i: number = 0; i <= reelIndex; i++) {
                for (let j = 0; j < this._reels[i].currentSymbols.length; j++) {
                    let symbol = this._reels[i].currentSymbols[j];

                    if (symbol && symbol.id + 1 != Settings.SymbolTeasing) {
                        symbol.goToCase(Symbol.CaseNowin);
                    }
                }
            }
        }

        this.emit(MachineExtended.OneReelRollFinished, reelIndex);

        if (this._finishedRollReelIndexes === this._reelCount) {
            this._finishedRollReelIndexes = 0;
            PlayList.stop("SND_BaseRotation");

            PlayList.stop("SND_BaseTeaser01");
            PlayList.stop("SND_BaseTeaser02");
            PlayList.stop("SND_BaseTeaser03");

            this.emit(MachineExtended.RollFinished);
        }
    }

    public onReelStopped(reelIndex: number): void {
        /*
        if (this._stoppedSymbolsArray[reelIndex].symbols.indexOf(13) !== -1) {
            let count = 0;

            for (let i = 0; i <= reelIndex; i++) {
                for (let j = 0; j <= this._stoppedSymbolsArray[i].symbols.length; j++) {
                    if (this._stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                        count++
                    }
                }
            }

            // 3 = symbol limit, 4 = reel count - 1
            if (3 - count <= 4 - reelIndex) {
                PlayList.play('SND_BaseScatter');
            }
        }

        this.tease(reelIndex);

        PlayList.play('SND_BaseRotationEnd' + reelIndex);
        */

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;
        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._reelCount) {
            PlayList.stop("SND_BaseRotation", 10);
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();
            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this.emit(Machine.MachineStopped);
            this._reelsContainer.visible = false;
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/